import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, ShieldCheck } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSession } from "@/hooks/useSession";
import { api, type NxModule } from "@/lib/api";

// Capability domains, per the Premium brief: modules grouped by what they
// protect — not by implementation detail.
const GROUPS: { label: string; match: (m: NxModule) => boolean }[] = [
  { label: "Observability", match: (m) => m.provides.some(p => ["monitoring", "observability", "prometheus-export", "metrics", "reporting", "export"].includes(p)) },
  { label: "Recovery", match: (m) => m.provides.some(p => ["backup", "recovery"].includes(p)) },
  { label: "Traffic protection", match: (m) => m.provides.some(p => ["ratelimit", "cache", "loadbalance", "maintenance", "pages", "openapi"].includes(p)) },
  { label: "Identity", match: (m) => m.provides.some(p => p.includes("identity") || p.includes("sso") || p.includes("oidc")) },
  { label: "Certificates", match: (m) => m.provides.some(p => p.includes("cert")) },
];

function groupOf(m: NxModule): string {
  return GROUPS.find((g) => g.match(m))?.label ?? "Other";
}

export default function Modules() {
  const qc = useQueryClient();
  const { user } = useSession();
  const canOperate = user?.role === "owner" || user?.role === "operator";
  const { data } = useQuery({
    queryKey: ["nx-modules"],
    queryFn: () => api.get<{ modules: NxModule[] }>("/api/modules"),
    refetchInterval: 10_000,
  });
  const mut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "enable" | "disable" }) =>
      api.post(`/api/modules/${id}/${action}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nx-modules"] }),
    onError: (e) => alert(String(e)),
  });

  const byGroup = new Map<string, NxModule[]>();
  for (const m of data?.modules ?? []) {
    const g = groupOf(m);
    byGroup.set(g, [...(byGroup.get(g) ?? []), m]);
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Native modules"
        description="NXShield-owned capabilities. Provider is always NXShield — enabling one never changes an upstream BunkerWeb plugin's license or state. Saved settings apply on enable; effective state comes from live probes."
      />
      {GROUPS.map((g) => {
        const mods = byGroup.get(g.label) ?? [];
        if (!mods.length) return null;
        return (
          <Card key={g.label}>
            <div className="border-b px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {g.label}
            </div>
            <div className="divide-y">
              {mods.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{m.title}</span>
                      <Badge variant="secondary" className="font-mono text-[10px]">{m.id}</Badge>
                      <Badge variant="outline" className="text-[10px]">v{m.version}</Badge>
                      {m.mandatory && <Badge variant="secondary" className="text-[10px]">platform</Badge>}
                      {m.needs_executor && <Badge variant="outline" className="text-[10px]">executor</Badge>}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                      <span>provides: {m.provides.join(", ")}</span>
                      <span>permissions: {m.permissions.join(", ") || "none"}</span>
                      {m.effective && (
                        <span className={m.effective.ok ? "text-emerald-400" : "text-amber-300"}>
                          effective: {m.effective.ok ? "healthy" : m.effective.detail}
                        </span>
                      )}
                      {!m.enabled && !m.mandatory && m.state !== "enabled" && (
                        <span>state: {m.state}</span>
                      )}
                      {m.last_error && <span className="text-red-400">error: {m.last_error}</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={m.enabled ? "default" : "secondary"}>
                      {m.enabled ? "enabled" : m.state}
                    </Badge>
                    {canOperate && !m.mandatory && (
                      m.enabled ? (
                        <Button size="sm" variant="ghost"
                          onClick={() => mut.mutate({ id: m.id, action: "disable" })}>
                          disable
                        </Button>
                      ) : (
                        <Button size="sm"
                          onClick={() => mut.mutate({ id: m.id, action: "enable" })}>
                          enable
                        </Button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
      {!data?.modules?.length && (
        <Card className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
          <Boxes className="h-4 w-4" /> No native modules registered.
          <ShieldCheck className="h-4 w-4" />
        </Card>
      )}
    </div>
  );
}
